import { motion } from "framer-motion";
import { ShieldCheck, Fingerprint, Lock, Timer } from "lucide-react";
import BrandName from "@/components/site/BrandName";

/**
 * Replaces the previous card-heavy Features block with a single, spacious
 * typography-first moment. Anchor ID kept as #features so nav still works.
 */

const pillars = [
  { icon: Lock, label: "Encrypted in transit and at rest" },
  { icon: Fingerprint, label: "Every event signed and chained" },
  { icon: Timer, label: "Retained for seven years, verifiable forever" },
  { icon: ShieldCheck, label: "UK region hosted, GDPR aligned" },
];

export default function FeaturesMoment() {
  return (
    <section
      id="features"
      data-testid="features-section"
      className="relative py-24 md:py-32 border-t border-white/[0.04]"
    >
      <div className="mx-auto max-w-5xl px-5 lg:px-8 text-center">
        <div className="eyebrow justify-center inline-flex">
          Trust, in one sentence
        </div>

        <motion.h2
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.7 }}
          className="mt-6 font-display text-[38px] sm:text-5xl md:text-[64px] tracking-[-0.04em] text-white leading-[1.02] font-semibold"
        >
          Every acknowledgement,{" "}
          <span className="text-[#D4A65A]">signed</span>. Every certificate,{" "}
          <span className="text-[#D4A65A]">verifiable</span>. Every event,{" "}
          <span className="text-[#D4A65A]">retained</span>.
        </motion.h2>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-6 text-slate-400 text-[15px] md:text-base leading-relaxed max-w-2xl mx-auto"
        >
          <BrandName /> was built the way regulated software has to be built.
          Not as an add-on. As the record itself.
        </motion.p>
      </div>

      <div className="mt-16 md:mt-20 mx-auto max-w-4xl px-5 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-8">
          {pillars.map((p, i) => {
            const Icon = p.icon;
            return (
              <motion.div
                key={p.label}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.35 }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="flex flex-col items-center text-center"
              >
                <Icon size={22} className="text-[#D4A65A]" />
                <div className="mt-3 text-[12.5px] text-slate-300 leading-relaxed max-w-[180px]">
                  {p.label}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
